import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import RehypeRaw from "rehype-raw";

import { Accordion, AccordionTab } from "primereact/accordion";
import { Skeleton } from "primereact/skeleton";

import ClickableDots from "sj-ui-toolkit/src/atlas/components/ClickableDots";

import { useInteraction, useSession } from "../contexts/SandboxHooks";
import { remarkVimeo } from "../helpers/markdown";
import CodeBlock from "./CodeBlock";

import S from "../_styles.module.scss";

// Height of the tabs + the dots
const InstructionNavHeight = 90;

const Instructions = ({ steps }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [resolvedHints, setResolvedHints] = useState({});
  const [activeHintIndices, setActiveHintIndices] = useState({});
  const stepRefs = useRef([]);
  const isScrollingRef = useRef(false);

  const { session } = useSession();
  const { reportStepsCompleted, useHint } = useInteraction();
  const hintsReady = session.readyTimestamp !== null;

  // Call reportStepsCompleted any time current step changes.
  useEffect(() => {
    reportStepsCompleted(currentStepIndex);
  }, [currentStepIndex, reportStepsCompleted]);

  const getDotState = index => {
    if (index < currentStepIndex) return "complete";
    if (index === currentStepIndex) return "in_progress";
    return "to_do";
  };

  const fetchHint = async hintId => {
    const response = await useHint(hintId);
    if (response) {
      setResolvedHints(prev => ({ ...prev, [hintId]: response.markdown || "Error loading hint" }));
    }
  };

  const onHintTabChange = (stepIndex, hints, e) => {
    setActiveHintIndices(prev => ({ ...prev, [stepIndex]: e.index }));

    if (!hintsReady) return;

    (e.index || []).forEach(hintIdx => {
      const hintId = hints[hintIdx]?.id;
      if (hintId && !resolvedHints[hintId]) {
        fetchHint(hintId);
      }
    });
  };

  // Fetch any open hints once the session becomes ready
  useEffect(() => {
    if (!hintsReady) return;

    steps.forEach((step, stepIndex) => {
      const openIndices = activeHintIndices[stepIndex] || [];
      openIndices.forEach(hintIdx => {
        const hintId = step.hints?.[hintIdx]?.id;
        if (hintId && !resolvedHints[hintId]) {
          fetchHint(hintId);
        }
      });
    });
  }, [hintsReady]);

  const navigateToStep = useCallback(
    index => {
      if (index === currentStepIndex) return;
      const scroller = document.querySelector("#lesson-left-panel-scroll");
      const stepRef = stepRefs.current[index];
      if (!scroller || !stepRef) return;

      isScrollingRef.current = true;
      const scrollTop = stepRef.offsetTop - InstructionNavHeight;
      scroller.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });

      // Calculate a timeout based on # of steps to scroll
      // Using log based on observed behavior of scrollTo in Chrome
      const timeout = 500 + Math.log(Math.abs(currentStepIndex - index)) * 400;

      // Update current step index
      setCurrentStepIndex(index);

      // Allow some time for scrolling to finish before re-enabling scroll tracking
      setTimeout(() => {
        isScrollingRef.current = false;
      }, timeout);
    },
    [currentStepIndex]
  );

  // Track scroll position to update current step
  useEffect(() => {
    const scroller = document.querySelector("#lesson-left-panel-scroll");
    if (!scroller) return;

    const handleScroll = () => {
      if (isScrollingRef.current) return;

      // Find out which step we've scrolled to
      let furthestStepIndex;

      // If we have scrolled as much as possible, set to last step
      if (scroller.scrollHeight - scroller.scrollTop === scroller.clientHeight) {
        furthestStepIndex = steps.length - 1;
      }
      // Otherwise, figure out which step is the last one above the scroll position
      else {
        furthestStepIndex = 0;

        const scrollTop = scroller.scrollTop + InstructionNavHeight;

        stepRefs.current.forEach((stepRef, index) => {
          if (!stepRef) return;

          // A little bit of wiggle room
          if (scrollTop >= stepRef.offsetTop - 3) {
            furthestStepIndex = index;
          }
        });
      }

      setCurrentStepIndex(furthestStepIndex);
    };

    scroller.addEventListener("scroll", handleScroll);

    return () => {
      scroller.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className={S.instructionsContainer}>
      <div className={S.instructionsNavigation}>
        <div className="flex items-center gap-0.5">
          {steps.map((step, index) => (
            <ClickableDots
              key={index}
              title={`Step ${index + 1}`}
              dotState={getDotState(index)}
              onClick={() => navigateToStep(index)}
            />
          ))}
        </div>
      </div>
      {steps.map((step, index) => (
        <div key={index} className={S.instructionStep} ref={el => (stepRefs.current[index] = el)}>
          <ReactMarkdown
            remarkPlugins={[remarkVimeo]}
            rehypePlugins={[RehypeRaw]}
            components={{
              pre: pre => <CodeBlock node={pre.node}>{pre.children}</CodeBlock>,
              code: code => <code className="atlas-code">{code.children}</code>,
              blockquote: ({ children }) => (
                <blockquote className="atlas-blockquote">{children}</blockquote>
              ),
            }}
          >
            {step.content.markdown}
          </ReactMarkdown>
          {step.hints?.length > 0 && (
            <Accordion
              multiple
              activeIndex={activeHintIndices[index] || []}
              onTabChange={e => onHintTabChange(index, step.hints, e)}
              aria-label="Hints"
            >
              {step.hints.map((hint, idx) => (
                <AccordionTab
                  key={idx}
                  headerTemplate={
                    <div className="flex items-center gap-2 justify-between w-full">
                      <span>{hint.title}</span>
                    </div>
                  }
                >
                  {resolvedHints[hint.id] ? (
                    <ReactMarkdown
                      rehypePlugins={[RehypeRaw]}
                      rel="noopener noreferrer"
                      components={{
                        pre: pre => {
                          return <CodeBlock node={pre.node}>{pre.children}</CodeBlock>;
                        },
                      }}
                    >
                      {resolvedHints[hint.id]}
                    </ReactMarkdown>
                  ) : (
                    <HintSkeleton />
                  )}
                </AccordionTab>
              ))}
            </Accordion>
          )}
        </div>
      ))}
    </div>
  );
};

const HintSkeleton = () => {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton width="100%" />
      <Skeleton width="75%" />
      <Skeleton width="90%" />
      <Skeleton width="80%" />
    </div>
  );
};

export default Instructions;
